import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // chamfer (.cut-corner) is applied per-variant — only on FILLED variants, where there's no
  // border for the clip to break. Bordered variants (outline) stay rounded with a clean edge.
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap text-sm font-medium outline-none transition-colors duration-150 disabled:pointer-events-none disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/45 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "cut-corner bg-primary text-primary-foreground shadow-soft hover:bg-primary/90 active:bg-primary/85",
        destructive: "cut-corner bg-destructive text-white shadow-soft hover:bg-destructive/90 active:bg-destructive/85 focus-visible:ring-destructive/25",
        outline: "rounded-lg border bg-background text-foreground hover:bg-accent hover:text-accent-foreground dark:bg-input/20 dark:border-input dark:hover:bg-input/30",
        secondary: "cut-corner bg-secondary text-secondary-foreground hover:bg-secondary/80",
        soft: "cut-corner bg-primary/[0.08] text-primary hover:bg-primary/[0.14]",
        ghost: "rounded-lg text-foreground/75 hover:bg-accent hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3.5 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 text-[13px] has-[>svg]:px-2.5",
        lg: "h-10 px-5 text-[15px] has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
)

function Button({
  className, variant, size, asChild = false, ...props
}: React.ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "button"
  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />
}

export { Button, buttonVariants }
